import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Chip, Checkbox, IconButton, Menu, MenuItem,
  Dialog, Slide, Typography
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { fetchData, deleteData } from "../../BaseUrl";
import DynamicTable from "../../DynamicComponent/DynamicTable";
import ReImbursementForm from "./ReImbursementForm";
import Toast from "../../DynamicComponent/Toast";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const generateFallback = () => {
  const statuses = ["Resolved", "Pending", "Inprogress", "Refund"];
  const teachers = [
    { name: "Mrs. Lakshmi", empId: "EMP001", grade: "Grade 3", section: "A" },
    { name: "Mr. Karthik", empId: "EMP002", grade: "Grade 4", section: "B" },
    { name: "Mrs. Anitha", empId: "EMP003", grade: "Grade 5", section: "A" },
    { name: "Mr. Venkat", empId: "EMP004", grade: "Grade 3", section: "C" },
    { name: "Mrs. Deepa", empId: "EMP005", grade: "Grade 4", section: "A" },
  ];
  const titles = ["Travel Expense", "Stationery", "Food Allowance", "Medical Claim", "Workshop Fee"];
  const rows = [];
  for (let i = 0; i < 15; i++) {
    const t = teachers[i % teachers.length];
    rows.push({
      id: null,
      teacherName: t.name,
      empId: t.empId,
      grade: t.grade,
      section: t.section,
      expenseTitle: titles[i % titles.length],
      date: `2025-0${(i % 9) + 1}-${String((i % 28) + 1).padStart(2, "0")}`,
      amount: (100 + i * 37).toFixed(2),
      receiptFile: `receipt_${i + 1}.pdf`,
      status: statuses[i % 4],
    });
  }
  return rows;
};

const statusStyle = (status) => {
  if (status === "Resolved") return { backgroundColor: "#e8f5e9", color: "#4caf50" };
  if (status === "Pending") return { backgroundColor: "#fff3e0", color: "#f57c00" };
  if (status === "Refund") return { backgroundColor: "#e0f2f1", color: "#00796b" };
  return { backgroundColor: "#e3f2fd", color: "#1976d2" };
};

const actionBtnStyle = {
  border: "1px solid #D5D7DA",
  borderRadius: "8px",
  color: "#8F95B2",
  textTransform: "none",
  fontSize: "13px",
  padding: "6px 14px",
};

function ActionMenu({ row, onView, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}><MoreVertIcon fontSize="small" /></IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { setAnchorEl(null); onView(row); }}>View</MenuItem>
        <MenuItem onClick={() => { setAnchorEl(null); onDelete(row); }} sx={{ color: "#f44336" }}>Delete</MenuItem>
      </Menu>
    </>
  );
}

function ViewReimbursementDialog({ record, onClose }) {
  if (!record) return null;
  const fields = [
    ["Teacher Name", record.teacherName || record.name],
    ["Employee ID", record.empId || record.employeeId],
    ["Expense Title", record.expenseTitle],
    ["Amount", record.amount ? `$${record.amount}` : "—"],
    ["Date", record.expenseDate ? record.expenseDate.substring(0, 10) : record.date],
    ["Status", record.status],
    ["Description", record.description],
  ].filter(([, v]) => v);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>Reimbursement Details</Typography>
        <Button onClick={onClose} size="small" sx={{ color: "#666", textTransform: "none" }}>Close</Button>
      </Box>
      {fields.map(([label, value]) => (
        <Box key={label} sx={{ display: "flex", gap: 1, mb: 1, borderBottom: "1px solid #f0f0f0", pb: 1 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "13px", color: "#555", minWidth: 140 }}>{label}:</Typography>
          <Typography sx={{ fontSize: "13px", color: "#333" }}>{value}</Typography>
        </Box>
      ))}
      {(record.receiptUrl || record.receiptFile) && (
        <Box sx={{ mt: 2 }}>
          <Button
            startIcon={<PictureAsPdfIcon sx={{ color: "#f44336" }} />}
            onClick={() => record.receiptUrl && window.open(record.receiptUrl, "_blank")}
            sx={{ color: "#1976d2", textTransform: "none", fontSize: "13px" }}
          >
            {record.receiptFile || "View Receipt"}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default function ReImbursement() {
  const [rawData, setRawData] = useState([]);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const handleView = useCallback((row) => setViewRow(row), []);
  const handleDeleteRequest = useCallback((row) => setDeleteRow(row), []);

  const handleDeleteConfirm = async () => {
    if (!deleteRow?.id) { setDeleteRow(null); return; }
    try {
      await deleteData(`teacher/reimbursement/${deleteRow.id}`);
      setToast({ open: true, severity: "success", message: "Reimbursement deleted" });
      setDeleteRow(null);
      loadData();
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Delete failed" });
      setDeleteRow(null);
    }
  };

  const buildRows = useCallback((data, onView, onDelete) => {
    return data.map((r) => ({
      checkbox: <Checkbox size="small" />,
      teacherName: r.teacherName || r.name,
      empId: r.empId || r.employeeId,
      grade: r.grade || "—",
      section: r.section || "—",
      expenseTitle: r.expenseTitle,
      date: r.expenseDate ? r.expenseDate.substring(0, 10) : r.date,
      amount: `$${r.amount}`,
      uploadReceipt: (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <PictureAsPdfIcon sx={{ color: "#f44336", fontSize: "16px" }} />
          <span
            style={{ color: "#1976d2", fontSize: "12px", cursor: r.receiptUrl ? "pointer" : "default" }}
            onClick={() => r.receiptUrl && window.open(r.receiptUrl, "_blank")}
          >
            {r.receiptFile || "receipt.pdf"}
          </span>
        </Box>
      ),
      statusChip: (
        <Chip label={r.status} size="small" sx={{ ...statusStyle(r.status), fontWeight: "bold", fontSize: "12px", borderRadius: "20px" }} />
      ),
      action: <ActionMenu row={r} onView={onView} onDelete={onDelete} />,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = useCallback(async () => {
    try {
      const response = await fetchData("teacher/reimbursement/list");
      const data = response?.data?.reimbursements && Array.isArray(response.data.reimbursements)
        ? response.data.reimbursements
        : (response?.data && Array.isArray(response.data) ? response.data : generateFallback());
      setRawData(data);
      setRows(buildRows(data, handleView, handleDeleteRequest));
    } catch (_) {
      const data = generateFallback();
      setRawData(data);
      setRows(buildRows(data, handleView, handleDeleteRequest));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildRows, handleView, handleDeleteRequest]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (rawData.length >= 0) setRows(buildRows(rawData, handleView, handleDeleteRequest));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawData]);

  const columns = [
    { label: "", field: "checkbox" },
    { label: "Teachers Name", field: "teacherName" },
    { label: "Emp ID", field: "empId" },
    { label: "Grade", field: "grade" },
    { label: "Section", field: "section" },
    { label: "Expense Title", field: "expenseTitle" },
    { label: "Date", field: "date" },
    { label: "Amount ($)", field: "amount" },
    { label: "Upload Receipt", field: "uploadReceipt" },
    { label: "Status", field: "statusChip" },
    { label: "Action", field: "action" },
  ];

  return (
    <Box sx={{ padding: "1rem", textAlign: "left" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <p style={{ color: "rgba(0,0,0,0.5)", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
        Pages / Re-Imbursement
      </p>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <h2 style={{ margin: 0 }}>Re-Imbursement</h2>
          <Chip label={`${rawData.length} Records`} size="small" sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: "bold", fontSize: "12px", borderRadius: "20px", padding: "4px 12px" }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button startIcon={<DeleteOutlineIcon />} sx={actionBtnStyle}>Delete</Button>
          <Button startIcon={<FilterListIcon />} sx={actionBtnStyle}>Filters</Button>
          <Button startIcon={<FileDownloadIcon />} sx={actionBtnStyle}>Export</Button>
          <Button startIcon={<AddIcon />} onClick={() => setFormOpen(true)} sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", borderRadius: "8px", padding: "8px 20px", fontSize: "13px" }}>
            + Add
          </Button>
        </Box>
      </Box>
      <DynamicTable columns={columns} rows={rows} />

      {/* Create Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} TransitionComponent={Transition} fullWidth maxWidth="sm">
        <ReImbursementForm handleClose={() => { setFormOpen(false); loadData(); }} />
      </Dialog>

      {/* View Dialog */}
      <Dialog open={Boolean(viewRow)} onClose={() => setViewRow(null)} fullWidth maxWidth="sm">
        <ViewReimbursementDialog record={viewRow} onClose={() => setViewRow(null)} />
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteRow)} onClose={() => setDeleteRow(null)} maxWidth="xs" fullWidth>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: 1 }}>Delete Reimbursement</Typography>
          <Typography sx={{ color: "#666", fontSize: "14px", mb: 3 }}>
            Are you sure you want to delete this reimbursement record? This action cannot be undone.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={() => setDeleteRow(null)} sx={{ color: "#666", textTransform: "none", border: "1px solid #D5D7DA", borderRadius: "8px", px: 3 }}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} sx={{ backgroundColor: "#f44336", color: "#fff", textTransform: "none", borderRadius: "8px", px: 3, "&:hover": { backgroundColor: "#d32f2f" } }}>Delete</Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
